import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "video_url": "https://example.com/input_video.mp4",
 *   "duration": "10",
 *   "aspect_ratio": "16:9",
 *   "extension_type": "forward",
 *   "seed": 42
 * }
 * ```
 */
export interface KlingDurationExtensionParams {
  /**
   * 视频URL（必填），必须是有效的HTTP或HTTPS URL
   */
  video_url: string;
  /**
   * 目标扩展时长（必填），支持: "5"（5秒）或 "10"（10秒），也可以是整数 5 或 10
   */
  duration: string | any;
  /**
   * 视频宽高比（可选），支持: 16:9（横屏）, 9:16（竖屏）, 1:1（方形）
   * @values 16:9 | 9:16 | 1:1
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  /**
   * 扩展类型（可选），支持: forward（前向扩展）, backward（后向扩展）, both（双向扩展）
   * @values forward | backward | both
   */
  extension_type?: 'forward' | 'backward' | 'both';
  /**
   * 视频质量参数（可选）
   */
  quality?: string;
  /**
   * 随机种子（可选），用于可重现的生成结果
   */
  seed?: number;
  /**
   * 外部任务ID（可选），用于关联外部系统的任务
   */
  external_task_id?: string;
}

export interface KlingDurationExtensionResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_duration_extension
 *
 * Kling AI 视频时长扩展接口，支持对现有视频进行前向、后向或双向时长扩展。
 * 
 * Model: kling-duration-extension
 * Features:
 * - 支持将视频扩展到5秒或10秒
 * - 支持三种宽高比：16:9（横屏）、9:16（竖屏）、1:1（方形）
 * - 支持三种扩展模式：forward（前向扩展）、backward（后向扩展）、both（双向扩展）
 * - 支持自定义随机种子
 * - 异步任务处理，自动轮询获取结果
 * - JWT Token 认证
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingDurationExtension } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingDurationExtension(
 *   {
 *     "video_url": "https://example.com/input_video.mp4",
 *     "duration": "10",
 *     "aspect_ratio": "16:9",
 *     "extension_type": "forward",
 *     "seed": 42
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingDurationExtensionParams 接口定义
 * @returns 生成的资源列表
 */
export async function klingDurationExtension(
  params: KlingDurationExtensionParams,
): Promise<KlingDurationExtensionResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_duration_extension',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingDurationExtensionResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
